import React, { useRef, useState, useEffect } from 'react';
import { Grid, List, ChevronLeft, ChevronRight } from 'lucide-react';
import { Avatar, Row, Col } from 'antd'; // Import Row and Col from Ant Design
import ProductCardComponent from '../../MyStore/components/Products/components/cardComponents/ProductCardComponent/index.js';
import EmailCardComponent from '../../MyStore/components/Products/components/cardComponents/EmailCardComponent/index.js';
import VideoCard from '../../MyStore/components/Products/components/cardComponents/VideoComponent/index.js';
import SpotifyCardComponent from '../../MyStore/components/Products/components/cardComponents/SpotifyCardComponent/index.js';
import AppleCardComponent from '../../MyStore/components/Products/components/cardComponents/AppleCardComponent/index.js';
import TipCardComponent from '../../MyStore/components/Products/components/cardComponents/TipCardComponent/index.js';
import CourseCardComponent from '../../MyStore/components/Products/components/cardComponents/CourseCardComponent/index.js';
import RaffleCardComponent from '../../MyStore/components/Products/components/cardComponents/RaffleCardComponent/index.js';
import {
    TabContent,
    TabsWrapper,
    StyledTabs,
    ProfileHeader,
    ShopHeader,
    ViewToggle,
    SectionTitleWrapper,
    ScrollableContainer,
    ScrollableShopContainer,
    ContentContainer,
    ContentWrapper,
    HeroAvatar,
    TabsContainer,
    ScrollableItem,
    SocialIconsContainer,
    SocialIconLink,
    LeftArrow,
    RightArrow,
    StyledShopCard
} from "./styled.js";
import { CheckCircleFilled } from '@ant-design/icons';
import { CHECKOUT_URL } from '../../../config.js';
import { observer } from 'mobx-react-lite';
import { useStore } from "../../../hooks/storeContext";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram, faYoutube, faTwitter, faTiktok, faDiscord, faTwitch, faPinterest, faFacebookF, faLinkedinIn, faSnapchat, faReddit, faWhatsapp, faTumblr, faSoundcloud, faGithub, faSpotify, faMedium, faThreads, faBluesky } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import SEO from '../../../components/SEO';
import sparkleLogo from '../../../assets/images/sparkle-black-logo.jpg';
import ImageCardComponent from '../../MyStore/components/Products/components/cardComponents/ImageCardComponent/index.js';
import EngagementCardComponent from '../../MyStore/components/Products/components/cardComponents/EngagementCardComponent/index.js';
import { trackCreatorPageView } from '../../../utils/tracking/googleAds';
import { trackCreatorPageView as trackTikTokCreatorPageView } from '../../../utils/tracking/tiktokPixel';

const StoreContent = observer(({ userData, theme, items, storeName, commerceProducts, isShopDisplayed }) => {
    const { authStore } = useStore();
    const [activeTab, setActiveTab] = useState('1');
    const [shopViewed, setShopViewed] = useState(false);
    const containerRef = useRef(null);
    const [viewMode, setViewMode] = useState('grid');
    const [tabsHeight, setTabsHeight] = useState(400);
    const hasPublishedProducts = commerceProducts.some(product => product.isPublished);
    const [scrollPosition, setScrollPosition] = useState(0);

    const iconMap = {
        instagram: faInstagram,
        facebook: faFacebookF,
        twitter: faTwitter,
        youtube: faYoutube,
        linkedin: faLinkedinIn,
        tiktok: faTiktok,
        threads: faThreads,
        snapchat: faSnapchat,
        bluesky: faBluesky,
        reddit: faReddit,
        whatsapp: faWhatsapp,
        tumblr: faTumblr,
        soundcloud: faSoundcloud,
        github: faGithub,
        spotify: faSpotify,
        medium: faMedium,
        twitch: faTwitch,
        pinterest: faPinterest,
        discord: faDiscord,
        email: faEnvelope
    };

    const seoData = {
        title: `${userData?.user.displayName || storeName || 'Creator'}'s Sparkle Store`,
        description: userData?.user.bio || `Explore unique products and content from ${userData?.user.displayName || storeName}!`,
        canonicalUrl: `https://sparkle.store/${storeName}`,
        image: userData?.user.avatar || sparkleLogo,
        structuredData: {
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            "mainEntity": {
                "@id": userData?.user.displayName || storeName,
                "@type": "Person",
                "name": userData?.user.displayName || 'Creator Name',
                "alternateName": `@${userData?.user.displayName || storeName} Sparkle Profile`,
                "identifier": userData?.user.displayName || storeName,
                "description": userData?.user.bio || "Creator's profile on Sparkle Store",
                "image": userData?.user.avatar || sparkleLogo,
                "sameAs": Object.values(userData?.socialLinks || {}).filter(link => link),
            },
            "url": `https://sparkle.store/${storeName}`,
            "significantLink": `https://sparkle.store/${storeName}/shop`,
            "isPartOf": {
                "@type": "WebApplication",
                "name": "Sparkle",
                "url": "https://sparkle.store",
            },
            "thumbnailUrl": userData?.user.avatar || 'https://sparkle.store/default-thumbnail.jpg',
            "genre": "ecommerce",
            "keywords": ["creator", "ecommerce", "store", "Sparkle", "link-in-bio"],
        },
    };

    const incrementShopView = async () => {
        if (!shopViewed) {
            try {
                await authStore.incrementShopViewCount(storeName);
                setShopViewed(true);
            } catch (error) {
                console.error('Failed to increment shop view:', error);
            }
        }
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
        if (key === '2' && !shopViewed) {
            incrementShopView();
        }
    };

    useEffect(() => {
        const updateTabsHeight = () => {
            const activeContent = document.querySelector('.active-tab-content');
            if (activeContent) {
                setTabsHeight(activeContent.offsetHeight);
            }
        };

        updateTabsHeight();
        window.addEventListener('resize', updateTabsHeight);
        return () => window.removeEventListener('resize', updateTabsHeight);
    }, [activeTab, items, commerceProducts]);

    useEffect(() => {
        const handleBeforeUnload = () => {
            setShopViewed(false);
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);    useEffect(() => {
        // Track creator page view when the component mounts
        trackCreatorPageView();
        trackTikTokCreatorPageView();
    }, []);

    const getAvatarText = () => {
        if (userData?.user.avatar) return null;
        if (storeName) return storeName.charAt(0).toUpperCase();
        if (userData?.user.fullName) return userData.user.fullName.charAt(0).toUpperCase();
        return '?';
    };

    const isHeroStyle = theme?.image_style === 'Hero';

    const renderLinksContent = () => {
        return items.map((item) => {
            if (item.type === 'headline') {
                return (
                    <div key={item.id} className="section">
                        <SectionTitleWrapper style={{ color: theme?.card_text_color }}>{item.text}</SectionTitleWrapper>
                    </div>
                );
            } else if (item.type === 'email') {
                return <EmailCardComponent key={item.id} item={item} theme={theme} formName={item.text} storeName={storeName} />;
            } else if (item.type === 'video') {
                return <VideoCard key={item.id} item={item} theme={theme} useCarousel={item.useCarousel} />;
            } else if (item.type === 'spotify') {
                return <SpotifyCardComponent key={item.id} item={item} theme={theme} />;
            } else if (item.type === 'apple') {
                return <AppleCardComponent key={item.id} item={item} theme={theme} />;
            } else if (item.type === 'tip') {
                return <TipCardComponent key={item.id} item={item} theme={theme} storeName={storeName} />;
            } else if (item.type === 'image') {
                // 1) Decide if item has creatorImagesV2 or fallback to creatorImages
                const images = (item.creatorImagesV2 && item.creatorImagesV2.length > 0)
                    ? item.creatorImagesV2
                    : (item.creatorImages || []);

                // 2) Also pass any optional title / subtitle (if you use them)
                return (
                    <ImageCardComponent
                        key={item.id}
                        theme={theme}
                        // Typically item.text is your "title"
                        title={item.text || item.title || ''}
                        subtitle={item.subtitle || item.descriptionBody ||''}
                        images={images}
                    />
                );
            } else if (item.type === 'engagement') {
                return <EngagementCardComponent key={item.id} item={item} theme={theme} storeName={storeName} />;
            } else if (item.type === 'course') {
                return <CourseCardComponent key={item.id} item={item} theme={theme} storeName={storeName} />;
            } else if (item.type === 'raffle') {
                return <RaffleCardComponent key={item.id} item={item} theme={theme} storeName={storeName} />;
            } else {
                return <ProductCardComponent key={item.id} item={item} theme={theme} />; //Digital, Custom, Link Products
            }
        });
    };

    const renderShopContent = () => {
        const toggleView = () => {
            setViewMode(prev => prev === 'scroll' ? 'grid' : 'scroll');
        };

        const handleProductClick = (e, product) => {
            e.preventDefault();
            const productId = product._id || product.id;
            const productType = product.type || 'creator';

            authStore.incrementProductClick(storeName, productId, productType);

            if (productType === 'commerce') {
                window.open(product.url, '_blank');
            } else {
                window.location.href = `${CHECKOUT_URL}/${storeName}/product/${product.slug}`;
            }
        };

        const renderCard = (product) => (
            <a
                href={product.type === 'commerce' ? product.url : `${CHECKOUT_URL}/${storeName}/product/${product.slug}`}
                onClick={(e) => handleProductClick(e, product)}
                target={product.type === 'commerce' ? "_blank" : "_self"}
                rel={product.type === 'commerce' ? "noopener noreferrer" : ""}
                style={{ textDecoration: 'none', color: 'inherit' }}
            >
                <StyledShopCard
                    hoverable
                    cover={<img
                        alt={product.title}
                        src={product.imageUrl || product.images[0]?.url}
                        style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', backgroundColor: 'white' }}
                    />}
                    theme={theme}
                >
                    <StyledShopCard.Meta
                        title={<span style={{
                            color: theme?.card_text_color,
                            fontSize: '16px',
                            whiteSpace: 'normal',
                            marginBottom: '0'
                        }}>
                            {product.title}
                        </span>}
                    />
                    <div style={{ marginTop: '8px' }}>
                        <p style={{
                            color: theme?.card_text_color,
                            fontSize: '14px'
                        }}>
                            ${product.price} {product.currency}
                        </p>
                    </div>
                </StyledShopCard>
            </a>
        );

        const scroll = (direction) => {
            if (containerRef.current) {
                const scrollAmount = direction === 'left' ? -300 : 300;
                containerRef.current.scrollBy({
                    left: scrollAmount,
                    behavior: 'smooth'
                });
            }
        };

        const handleScroll = () => {
            if (containerRef.current) {
                setScrollPosition(containerRef.current.scrollLeft);
            }
        };

        return (
            <>
                <ShopHeader>
                    <h3 style={{ color: theme?.font_color }}>
                        Products <span style={{ fontSize: '18px' }}>({commerceProducts.filter(p => p.isPublished).length})</span>
                    </h3>
                    <ViewToggle
                        onClick={toggleView}
                        active={viewMode === 'grid'}
                        title={viewMode === 'scroll' ? 'Switch to grid view' : 'Switch to scroll view'}
                        style={{ color: theme?.font_color }}
                    >
                        {viewMode === 'scroll' ? <Grid size={24} /> : <List size={24} />}
                    </ViewToggle>
                </ShopHeader>

                {viewMode === 'scroll' ? (
                    <ScrollableShopContainer ref={containerRef} onScroll={handleScroll}>
                        {scrollPosition > 0 && (
                            <LeftArrow onClick={() => scroll('left')}>
                                <ChevronLeft size={24} />
                            </LeftArrow>
                        )}
                        {commerceProducts.filter(product => product.isPublished).map((product) => (
                            <ScrollableItem key={product.id}>
                                {renderCard(product)}
                            </ScrollableItem>
                        ))}
                        {containerRef.current && (containerRef.current.scrollWidth - containerRef.current.clientWidth - scrollPosition > 1) && (
                            <RightArrow onClick={() => scroll('right')}>
                                <ChevronRight size={24} />
                            </RightArrow>
                        )}
                    </ScrollableShopContainer>
                ) : (
                    <Row gutter={[16, 16]} justify="start">
                        {commerceProducts.filter(product => product.isPublished).map((product) => (
                            <Col
                                key={product.id}
                                xs={12} // 2 columns on extra small screens
                                sm={8}  // 3 columns on small screens
                                md={8}  // 3 columns on medium screens
                                lg={8}  // 3 columns on large screens
                                xl={8}  // 3 columns on extra large screens
                            >
                                {renderCard(product)}
                            </Col>
                        ))}
                    </Row>
                )}
            </>
        );
    };

    return (
        <>
            <SEO
                title={seoData.title}
                description={seoData.description}
                canonicalUrl={seoData.canonicalUrl}
                image={seoData.image}
                structuredData={seoData.structuredData}
            />
            <ContentContainer
                style={{
                    background: isHeroStyle
                        ? theme?.background_color // Use solid background when hero image is used
                        : theme?.background_type === 'image' && theme?.background_image
                            ? `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.3)), url(${theme.background_image}) no-repeat center center / cover`
                            : theme?.background_type === 'gradient'
                                ? `linear-gradient(to top, ${theme.background_color} 60%, white 100%)`
                                : theme?.background_color,
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                }}
            >
                <ProfileHeader imageStyle={theme?.image_style}>
                    {isHeroStyle && (
                        <HeroAvatar
                            src={userData?.user.avatar}
                            backgroundColor={theme?.background_color || 'transparent'}
                        />
                    )}
                    {!isHeroStyle && (
                        <Avatar
                            size={100}
                            src={userData?.user.avatar || null}
                            style={{
                                background: userData?.user.avatar ? 'none' : '#000',
                                fontSize: '32px',
                                fontWeight: '600',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                textAlign: 'center',
                            }}
                        >
                            {getAvatarText()}
                        </Avatar>
                    )}
                    <h1 style={{
                        color: theme?.font_color,
                        position: isHeroStyle ? 'relative' : 'static',
                        marginTop: '27px',
                        fontSize: '24px',
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        {userData?.user.displayName || ''}
                        {userData?.user.isVerified && (
                            <CheckCircleFilled
                                style={{
                                    marginLeft: '5px',
                                    color: '#ffcd00',
                                    fontSize: '18px'
                                }}
                            />
                        )}
                    </h1>
                    <p style={{ marginBottom: '10px', maxWidth: '310px', color: theme?.font_color, position: isHeroStyle ? 'relative' : 'static' }}>{userData?.user.bio || ''}</p>
                    <ScrollableContainer>
                        <SocialIconsContainer>
                            {Object.entries(userData?.user.socialLinks || {}).map(([name, url]) => {
                                const icon = iconMap[name];
                                if (!icon || !url) return null;

                                const absoluteUrl = url.startsWith('http') ? url : `https://${url}`;

                                return (
                                    <SocialIconLink
                                        href={absoluteUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        key={name}
                                        theme={theme}
                                    >
                                        <FontAwesomeIcon icon={icon} />
                                    </SocialIconLink>
                                );
                            })}
                        </SocialIconsContainer>
                    </ScrollableContainer>
                </ProfileHeader>

                {(isShopDisplayed && hasPublishedProducts) ? (
                    <TabsContainer>
                        <StyledTabs
                            defaultActiveKey="1"
                            activeKey={activeTab}
                            onChange={handleTabChange}
                            centered
                            theme={theme}
                            items={[
                                {
                                    key: '1',
                                    label: 'Links',
                                    children: (
                                        <TabsWrapper style={{ height: tabsHeight }}>
                                            <TabContent active={activeTab === '1'} className={activeTab === '1' ? 'active-tab-content' : ''}>
                                                <ContentWrapper>
                                                    {renderLinksContent()}
                                                </ContentWrapper>
                                            </TabContent>
                                        </TabsWrapper>
                                    ),
                                },
                                {
                                    key: '2',
                                    label: 'Shop',
                                    children: (
                                        <TabsWrapper style={{ height: tabsHeight }}>
                                            <TabContent active={activeTab === '2'} className={activeTab === '2' ? 'active-tab-content' : ''}>
                                                <ContentWrapper>
                                                    {renderShopContent()}
                                                </ContentWrapper>
                                            </TabContent>
                                        </TabsWrapper>
                                    ),
                                },
                            ]}
                        />
                    </TabsContainer>
                ) : (
                    <ContentWrapper>
                        {renderLinksContent()}
                    </ContentWrapper>
                )}
            </ContentContainer>
        </>
    );
});

export default StoreContent;
